// PATH: src/components/curriculum/tracks.ts
/**
 * Filières : règles pures partagées par l'arbre du curriculum, les offres et
 * l'éditeur de document. Une offre sans filière vaut pour toutes les filières
 * du niveau (contrat : `track_ids` vide = toutes).
 */
import type { Track } from "../../models/domain";

export const offeringAppliesToTrack = (
  offering: { track_ids?: string[] | null },
  trackId: string,
): boolean => {
  const ids = offering.track_ids ?? [];
  return ids.length === 0 || ids.includes(trackId);
};

/** Tri du catalogue : `sort_order`, puis nom. */
export const sortTracks = (tracks: Track[]): Track[] =>
  [...tracks].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name),
  );

/**
 * Libellé des filières d'une offre. Une filière inconnue (supprimée, autre
 * niveau) est montrée par son identifiant plutôt que cachée.
 */
export const formatOfferingTracks = (
  trackIds: string[] | null | undefined,
  tracks: Track[],
): string => {
  const ids = trackIds ?? [];
  if (ids.length === 0) return "Toutes les filières";
  const byId = new Map(tracks.map((t) => [t.id, t]));
  return ids.map((id) => byId.get(id)?.name ?? id).join(", ");
};

/**
 * Libellé d'une offre dans l'arbre : nom de la matière, suivi des codes des
 * filières si l'offre est restreinte (« Comptabilité (SE, SGC) »). Une offre
 * ouverte à tout le niveau n'a pas de suffixe. Une filière inconnue est
 * montrée par son identifiant.
 */
export const formatOfferingLabel = (
  subjectName: string,
  trackIds: string[] | null | undefined,
  tracks: Track[],
): string => {
  const ids = trackIds ?? [];
  if (ids.length === 0) return subjectName;
  const byId = new Map(tracks.map((t) => [t.id, t]));
  return `${subjectName} (${ids.map((id) => byId.get(id)?.code ?? id).join(", ")})`;
};

/** Libellé de la filière d'un document : nom, « Commun » si null. */
export const formatDocumentTrack = (
  trackId: string | null | undefined,
  tracks: Track[],
): string => {
  if (!trackId) return "Commun";
  return tracks.find((t) => t.id === trackId)?.name ?? trackId;
};
